/**
 * **Eine Gruppe klappt auf und zu** — die Gruppen der Seitenleiste („Heute & Danach“, „Entwicklung“,
 * „Favoriten“, „Projekte“). Gewählt im Prototyp `tools/sidebar-groups-motion-prototype`,
 * Variante C (02.10.2026): erst der Platz, dann die Einträge; zu erst die Einträge, dann der Platz.
 *
 * **Der Takt ist `growMotion` im Takt der Breadcrumb** (Maß 0,15 s, Inhalt 0,2 s nach 0,05 s,
 * rückwärts das Maß 0,1 s nach dem Inhalt). Anders als dort liegen Platz und Inhalt auf ZWEI
 * Ebenen: Die Hülle fährt die Höhe, der Inhalt darin blendet aus 4 px oben ein — wie bisher beim
 * Aufklappen (`sectionSlideDown`). Eine Skalierung wie bei `growMotion` stauchte die Einträge.
 *
 * **Die Höhe ist eine gemessene Zahl, nicht `auto`** — `auto` springt im letzten Bild
 * (`useContentHeight`). Ohne Messung (jsdom, versteckter Tab) bleibt es bei `auto`, ohne Bewegung.
 *
 * **Zu bleibt ein Rest stehen** (`closedHeight`): der Abstand unter dem Titel einer eingeklappten
 * Gruppe. Der Inhalt darin ist dann unsichtbar; so fährt auch dieser Abstand mit, statt zu springen.
 *
 * Der Inhalt fährt einen ganzen `transform`: Die Web Animations API trägt ihn im Compositor weiter,
 * auch wenn die Seitenleiste gleichzeitig rendert (siehe `revealSequence`).
 *
 * Bewusst ohne DOM-Bezug: hier gerechnet, in `SidebarSection` eingesetzt.
 */

import type { TargetAndTransition } from 'framer-motion'
import { GROW_EASE, GROW_SIZE_DURATION } from './growMotion'

export interface CollapseMotionOptions {
  /** Gemessene Höhe des offenen Inhalts in Pixeln; `null` heißt `auto`, ohne Bewegung. */
  height: number | null
  /** Höhe der zugeklappten Hülle. Vorgabe 0. */
  closedHeight?: number
  /** Für `prefers-reduced-motion`: Die Zustände stehen sofort da. */
  reduced?: boolean
}

export interface CollapseMotion {
  clip: { open: TargetAndTransition; closed: TargetAndTransition }
  body: { open: TargetAndTransition; closed: TargetAndTransition }
}

/** Inhalt: Dauer, Verzug beim Aufklappen, Strecke aus oben (Breadcrumb-Takt, `sectionSlideDown`). */
const CONTENT = 0.2
const ENTER_DELAY = 0.05
const EXIT_DELAY = 0.1
const DISTANCE = 4

const SHOWN = 'translateY(0px)'
const HIDDEN = `translateY(-${DISTANCE}px)`

/**
 * @example
 * const [measureRef, height] = useContentHeight(true)
 * const m = collapseMotion({ height, closedHeight: 8 })
 * <motion.div initial={false} animate={open ? m.clip.open : m.clip.closed}>
 *   <motion.div ref={measureRef} initial={false} animate={open ? m.body.open : m.body.closed}>…</motion.div>
 * </motion.div>
 */
export function collapseMotion({ height, closedHeight = 0, reduced = false }: CollapseMotionOptions): CollapseMotion {
  const openHeight = height ?? 'auto'
  if (reduced) {
    const now = { duration: 0 }
    return {
      clip: { open: { height: openHeight, transition: now }, closed: { height: closedHeight, transition: now } },
      body: { open: { opacity: 1, transform: SHOWN, transition: now }, closed: { opacity: 0, transform: HIDDEN, transition: now } },
    }
  }
  return {
    clip: {
      open: { height: openHeight, transition: { duration: GROW_SIZE_DURATION, ease: GROW_EASE } },
      // Der Platz fällt erst, wenn die Einträge schon gehen — sonst schnitte er sie ab.
      closed: { height: closedHeight, transition: { duration: GROW_SIZE_DURATION, ease: GROW_EASE, delay: EXIT_DELAY } },
    },
    body: {
      open: { opacity: 1, transform: SHOWN, transition: { duration: CONTENT, ease: GROW_EASE, delay: ENTER_DELAY } },
      closed: { opacity: 0, transform: HIDDEN, transition: { duration: CONTENT, ease: GROW_EASE } },
    },
  }
}
